import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { TenantContext } from "../tenant";
import { Capacitor } from "@capacitor/core";
import { nativeApplicationAdapter } from "./custom-adapter/native-application-adapter";
import { PLATFORM, useIsNative } from "../utils";
import Keycloak, { KeycloakInitOptions } from "keycloak-js";
import { registerFetchInterceptor } from "./fetch-interceptor";

interface AuthProviderProps {
  onLogoutCallbackFn?: () => void;
  onLoginSuccess?: () => void;
  children?: React.ReactNode;
}

interface KeycloakContextValue {
  keycloak: Keycloak;
  initialized: boolean;
}

const AuthContext = createContext<KeycloakContextValue>({
  // @ts-ignore
  keycloak: undefined,
  initialized: false
});

export const useAuthContext = () => {
  try {
    return useContext(AuthContext);
  } catch (e) {
    return { keycloak: {} as unknown as Keycloak, initialized: false };
  }
};

export const AuthKeycloakProvider: FC<
  PropsWithChildren<{
    keycloak: Keycloak;
    initOptions: KeycloakInitOptions;
    onLogoutCallbackFn: () => void;
    onLoginCallbackFn: (keycloak: Keycloak) => void;
  }>
> = ({ keycloak, initOptions, onLogoutCallbackFn, onLoginCallbackFn, children }) => {
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (keycloak && !initialized) {
      keycloak.onAuthSuccess = () => onLoginCallbackFn(keycloak);
      keycloak.onAuthLogout = () => onLogoutCallbackFn;

      keycloak
        .init(initOptions)
        .then(() => {
          setInitialized(true);
        })
        .catch((reason) => {
          alert(`onInitError - ${JSON.stringify(reason)}`);
          console.error(`onInitError - ${JSON.stringify(reason)}`);
        });
    }
  }, [keycloak, onLoginCallbackFn, onLogoutCallbackFn, initOptions, initialized]);
  const authProviderValue = useMemo(() => ({ keycloak, initialized }), [keycloak, initialized]);

  return <AuthContext.Provider value={authProviderValue}>{children}</AuthContext.Provider>;
};

export const AuthProvider: React.FC<PropsWithChildren<AuthProviderProps>> = ({
  children,
  onLogoutCallbackFn,
  onLoginSuccess
}) => {
  const { keycloak, routesWithAuthToken, keycloakNative } = useContext(TenantContext);
  const isNative = useIsNative();

  const initOptions = useMemo(() => {
    let options: KeycloakInitOptions = {
      useNonce: true,
      onLoad: "check-sso",
      adapter: "default",
      silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html"
    };

    if (isNative) {
      options.adapter = nativeApplicationAdapter(keycloak);
      options.silentCheckSsoFallback = false;

      if (Capacitor.getPlatform() === PLATFORM.ANDROID) {
        options.redirectUri = keycloakNative?.androidReturnURL;
      } else if (Capacitor.getPlatform() === PLATFORM.IOS) {
        options.redirectUri = keycloakNative?.iosReturnURL;
        options.responseMode = "query";
      }
    }

    return options;
  }, [keycloak, isNative, keycloakNative?.androidReturnURL, keycloakNative?.iosReturnURL]);

  const onLoginCallbackFn = useCallback(
    (keycloak: Keycloak) => {
      console.info("onLoginCallbackFn enter");
      if (keycloak) {
        console.info("registerFetchInterceptor for auth");
        registerFetchInterceptor(keycloak, routesWithAuthToken);
        onLoginSuccess?.();
      }
    },
    [routesWithAuthToken, onLoginSuccess]
  );

  const onLogoutCallbackFnWrapper = useCallback(() => {
    console.info("onLogoutCallbackFn");
    onLogoutCallbackFn?.();
  }, [onLogoutCallbackFn]);

  return (
    <AuthKeycloakProvider
      keycloak={keycloak}
      initOptions={initOptions}
      onLogoutCallbackFn={onLogoutCallbackFnWrapper}
      onLoginCallbackFn={onLoginCallbackFn}
    >
      {children}
    </AuthKeycloakProvider>
  );
};
